import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  LibraryAdd as LibraryAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { sacolaService, ProdutoSacola } from '../../services/sacola';

interface SacolaFormProps {
  id?: number;
  readOnly?: boolean;
}

const SacolaForm: React.FC<SacolaFormProps> = ({ id, readOnly = false }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [codigoBarra, setCodigoBarra] = useState('');
  const [produtos, setProdutos] = useState<ProdutoSacola[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [codigoLiberar, setCodigoLiberar] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const saveMutation = useMutation({
    mutationFn: () => sacolaService.salvar(produtos),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Sacola salva com sucesso.', severity: 'success' });
      navigate('/Sacola');
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: 'Erro ao salvar sacola: ' + (error.message || error), severity: 'error' });
    },
  });

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const adicionarProduto = async () => {
    if (!codigoBarra) return;
    const enviar = codigoBarra;
    setCodigoBarra('');
    focusInput();

    const codigoEncontrado = produtos.find((p) => p.codigoBarras === enviar);
    if (codigoEncontrado) {
      setSnackbar({ open: true, message: `O código de barras: ${enviar} já se encontra nesta sacola.`, severity: 'error' });
      return;
    }

    try {
      const res = await sacolaService.buscaCodigoBarras(enviar);
      if (res == null) {
        setSnackbar({ open: true, message: `O código de barras: ${enviar} não existe no sistema.`, severity: 'error' });
      } else if (Array.isArray(res) && res.length > 0 && res[0].status === 412) {
        setCodigoLiberar(enviar);
        setDialogOpen(true);
      } else {
        const novoProduto: ProdutoSacola = {
          linha: produtos.length + 1,
          idCodigoBarras: res.idCodigoBarras,
          idProduto: res.idProduto,
          codigoBarras: res.codigoBarras,
          referencia: res.referencia,
          nomeProduto: res.nomeProduto,
          idEntrada: res.idEntrada,
        };
        setProdutos([novoProduto, ...produtos]);
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erro ao buscar código de barras: ' + (error.message || error), severity: 'error' });
    }
  };

  const liberarCodigo = async () => {
    try {
      const res = await sacolaService.liberarCodigoBarras(codigoLiberar);
      if (res == null) {
        setSnackbar({ open: true, message: 'O código de barras não existe no sistema.', severity: 'error' });
      } else if (Array.isArray(res) && res.length > 0 && res[0].status === 412) {
        setSnackbar({ open: true, message: res[0].detail, severity: 'error' });
      } else {
        const novoProduto: ProdutoSacola = {
          linha: produtos.length + 1,
          idCodigoBarras: res.idCodigoBarras,
          idProduto: res.idProduto,
          codigoBarras: res.codigoBarras,
          referencia: res.referencia,
          nomeProduto: res.nomeProduto,
          idEntrada: res.idEntrada,
        };
        setProdutos([novoProduto, ...produtos]);
        setDialogOpen(false);
        setCodigoLiberar('');
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erro ao liberar código de barras: ' + (error.message || error), severity: 'error' });
    }
  };

  const handleSubmit = () => {
    if (produtos.length === 0) {
      setSnackbar({ open: true, message: 'Adicione pelo menos um produto na sacola.', severity: 'error' });
      return;
    }
    saveMutation.mutate();
  };

  const handleVoltar = () => {
    navigate('/Sacola');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {id ? `Editar Sacola ${id}` : 'Nova Sacola'}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleVoltar}>
          Voltar
        </Button>
      </Box>

      {!readOnly && (
        <TextField
          fullWidth
          inputRef={inputRef}
          label="Adicionar produto"
          placeholder="Código de barras"
          value={codigoBarra}
          onChange={(e) => setCodigoBarra(e.target.value)}
          onBlur={adicionarProduto}
          onKeyDown={(e) => e.key === 'Enter' && adicionarProduto()}
          slotProps={{
            input: {
              endAdornment: <LibraryAddIcon color="action" />,
            },
          }}
          sx={{ mb: 3 }}
        />
      )}

      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Código de Barras</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nota Entrada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {produtos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">Nenhum produto adicionado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                produtos.map((produto) => (
                  <TableRow key={produto.linha}>
                    <TableCell>{produto.linha}</TableCell>
                    <TableCell>{produto.codigoBarras}</TableCell>
                    <TableCell>{produto.referencia}</TableCell>
                    <TableCell>{produto.nomeProduto}</TableCell>
                    <TableCell>{produto.idEntrada}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleVoltar} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saveMutation.isPending || produtos.length === 0}
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Código de barras bloqueado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja liberar o código <b>{codigoLiberar}</b> para utilização?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setCodigoLiberar('');
            }}
          >
            Cancelar
          </Button>
          <Button onClick={liberarCodigo} variant="contained" color="primary">
            Liberar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SacolaForm;
